import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, ViewChild, ViewContainerRef, inject, computed } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';

import { DataExplorerComponent } from './data-explorer/data-explorer.component';
import { CommonService } from '../../services/common.service';
import { RightSidebarComponent } from './right-sidebar/right-sidebar.component';
import { LabelService } from '../../services/label.service';
import { Dashboard, LabelTypes } from '../../shared/label.types';
import { ConversationOverviewComponent } from './conversation-overview/conversation-overview.component';
import * as Types from '../../shared/types';
import { TabularViewComponent } from './tabular-view/tabular-view.component';
import { DaterangePickerComponent } from '../../components/date-range-picker/date-range-picker.component';
import { FilterSectionComponent } from '../../components/filter-section/filter-section.component';
import { SharedDataService } from '../../services/shared-data.service';
import { transformColumnPreferences, transformTestToFilters } from '../../shared/appUtil';
import { FilterService } from '../../services/filter.service';
import { MoreDropDownComponent } from './more-drop-down/more-drop-down.component';
import { ManageColumnsComponent } from '../../components/manage-columns/manage-columns.component';
import { SearchDropdownComponent } from './search-dropdown/search-dropdown.component';
import { DashboardOptions } from '../../shared/models/dashboard.types';
import { BottomSheetDataService } from '../../services/bottom-sheet-data.service';
import { ReportComponent } from '../../components/report/report.component';
import { TimezoneComponent } from '../../components/timezone/timezone.component';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    DataExplorerComponent,
    RightSidebarComponent,
    ConversationOverviewComponent,
    TabularViewComponent,
    FilterSectionComponent,
    MoreDropDownComponent,
    ManageColumnsComponent,
    SearchDropdownComponent,
    TimezoneComponent,
  ],
  providers: [CommonService],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardComponent implements OnInit {
  private _bottomSheetDataService = inject(BottomSheetDataService);

  constructor(
    public commonService: CommonService,
    public labelService: LabelService,
    public sharedDataService: SharedDataService,
    private filterService: FilterService,
    public notificationService: NotificationService
  ) {}

  @ViewChild(TabularViewComponent) tableComponent!: TabularViewComponent;
  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;

  isChatBoxVisible: boolean = false;
  dashboard: Dashboard = {} as Dashboard;
  activeTab: string = 'visualization'; // Default active tab
  protected _tableViewFlag = computed<boolean>(() => this.sharedDataService.tableViewFlagStatus());

  // tableViewFlag: boolean = this.sharedDataService.tableViewFlag;
  columnLoading: boolean = false;
  tableViewBody: Types.TableViewBody = {
    filters: [],
  } as Types.TableViewBody;
  content: Array<{ label: string; value: string }> = [
    { label: 'option1', value: 'option1' },
    { label: 'option2', value: 'option2' },
    { label: 'option3', value: 'option3' },
  ];
  selectedValue = { value: 'contains', label: 'contains' };
  buttonTitle: string = 'Hide Fields';
  // userColumnKey = 'user_column_preference';
  // columnPreferenceDeletedFlag = 'column_preference_deleted_flag';
  private currentChildComponentRef: any; // Store reference to the current child component
  private subscription: Subscription | undefined;

  ngOnInit() {
    this.labelService.getLabelData().subscribe((data: LabelTypes) => {
      this.dashboard = data?.dashboard ?? {};
    });

    this.filterService.getRefreshDataStatus().subscribe((status) => {
      if (status) {
        this.tableViewBody.filters = this.populateFilteredData();
        this.fetchOverview();
        this.sharedDataService.updateDashboardState('filters', this.filterService.getFilterConditionDataValue());
        //this.fetchFilters();
      }
    });

    this.sharedDataService.filterOnly().subscribe((value) => {
      if (!value) {
        this.initializeChild();
      }
    });

    this.subscription = this.sharedDataService.getDateRange().subscribe((date) => {
      this.tableViewBody.start_date = date.startAPIDate;
      this.tableViewBody.end_date = date.endAPIDate;
      this.tableViewBody.timeZone = this.sharedDataService.selectedTimezoneValue;

      if (date?.startAPIDate && date.dateSelected) {
        this.fetchOverview();
      }
    });

    this.sharedDataService.getDashboardState().subscribe((dashboardData) => {
      this.sharedDataService.setTableViewFlagStatus(!!dashboardData.tableViewFlag);

      if (dashboardData.filters.length) {
        this.filterService.updateFilterData(dashboardData.filters);
      }
    });
    this.fetchColumnNames();
    this.fetchFilters();
  }

  ngAfterViewInit() {
    this.initializeChild();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  initializeChild() {
    // Destroy the previous component if it exists
    if (this.currentChildComponentRef) {
      this.currentChildComponentRef.destroy();
    }

    this.currentChildComponentRef = this.container?.createComponent(DaterangePickerComponent);
  }

  //download table data csv file
  onDownload() {
    if (this._tableViewFlag()) {
      this.tableComponent.exportPlot();
    }
  }

  viewSavedFilters() {}

  hideInsightsOverview() {}

  fetchColumnNames = async () => {
    this.columnLoading = true;
    this.commonService.getUserColumnPreference({}).subscribe({
      next: (names: Array<Types.ColumnPreferenceType>) => {
        this.sharedDataService.setColumnNames(transformColumnPreferences(names));
        this.columnLoading = false;
      },
      error: () => {
        this.columnLoading = false;
      },
    });
  };

  fetchOverview = () => {
    this.commonService.fetchOverview(this.tableViewBody).subscribe({
      next: (data: Types.Overview) => {
        this.sharedDataService.setConversationOverview(data);
      },
      error: (error) => {
        console.error('API Error:', error);
      },
    });
  };

  populateFilteredData(): Types.FilterRequest[] {
    const filterDataArray = this.filterService.getFilterConditionDataValue();
    const { data, error } = transformTestToFilters(filterDataArray);
    const filteredData: Types.FilterRequest[] = [...data];
    if (error) {
      this.notificationService.error('Error in filters', 'Error in filters');
    }
    return filteredData;
  }

  fetchFilters = () => {
    this.commonService.fetchDataFiltersInsights({}).subscribe({
      next: (data: Types.DataFilters) => {
        this.filterService.updateAllFilters(data);
      },
      error: (error) => {
        console.error('Error fetching all filters', error);
      },
    });

    this.commonService.fetchDataFiltersClusters({}).subscribe({
      next: (data: Types.DataFilters) => {
        this.filterService.updateAllFilters(data);
      },
      error: (error) => {
        console.error('Error fetching all filters', error);
      },
    });

    this.commonService.fetchDataFiltersConnectAttributes({}).subscribe({
      next: (data: Types.DataFilters) => {
        this.filterService.updateAllFilters(data);
      },
      error: (error) => {
        console.error('Error fetching all filters', error);
      },
    });
  };

  setSelectedDomain(filteredData: any[]) {
    const domainFilter = filteredData.find((item) => item.columnName === 'Domain');

    if (domainFilter) {
      // If 'Domain' column is found, set selectedDomain to its value
      this.commonService.selectedDomain = domainFilter.value;
    } else {
      // If 'Domain' column is not found, set selectedDomain to an empty string
      this.commonService.selectedDomain = '';
    }
  }

  handleItemSelect = (value: { label: string; value: string }) => {
    this.selectedValue = value;
  };

  onValueChange(newValue: any) {
    this.isChatBoxVisible = newValue;
  }

  showTab(tab: string): void {
    this.activeTab = tab;
  }

  showChartView(): void {
    if (this._tableViewFlag()) {
      this.sharedDataService.setTableViewFlagStatus(false);
    }
  }

  showDataView(): void {
    if (!this._tableViewFlag()) {
      this.sharedDataService.setTableViewFlagStatus(true);
    }
  }

  toggleDropdown() {}

  private onCustomerReportOptionClick() {
    this.filterService.checkCustomerEndpointSelected();
    this._bottomSheetDataService.setBottomSheetUiConfig({
      title: 'Customer Report',
      contentComponent: ReportComponent,
    });
    setTimeout(() => {
      this._bottomSheetDataService.setBottomSheetExpandedState('maximized');
    }, 100);
  }

  protected onDashboardOptionsClick(option: DashboardOptions) {
    switch (option) {
      case 'download_csv': {
        this.onDownload();
        return;
      }

      case 'customer_report': {
        this.onCustomerReportOptionClick();
        return;
      }

      default:
        return;
    }
  }
}
